import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  useTheme,
} from '@mui/material';
import {
  LockOutlined as LockOutlinedIcon,
  CenterFocusStrong as CenterFocusStrongIcon,
  MoneyOffOutlined as MoneyOffOutlinedIcon,
  ChatOutlined as ChatOutlinedIcon,
} from '@mui/icons-material';

const About = () => {
  const theme = useTheme();

  const values = [
    {
      icon: <LockOutlinedIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'Your data stays yours',
      description:
        'Where an app can do its work on the device, it should. CodePeek decodes every scan locally and sends nothing to a server. We build that way by default.',
    },
    {
      icon: <CenterFocusStrongIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'One job, done well',
      description:
        'We would rather ship a small app that nails a single task than a bloated one that half-does ten. Fewer buttons, fewer surprises.',
    },
    {
      icon: <MoneyOffOutlinedIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'No dark patterns',
      description:
        'No forced sign-ups, no nagging, no selling your details on. If an app carries an ad to keep it free, we say so plainly.',
    },
    {
      icon: <ChatOutlinedIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'We actually reply',
      description:
        'Being small has an upside: when you email us, a person reads it. Bug reports and feature ideas genuinely shape what we build next.',
    },
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'white',
          py: 8,
          textAlign: 'center',
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h1" gutterBottom sx={{ fontWeight: 700 }}>
            About Teniqua Systems
          </Typography>
          <Typography variant="h5" sx={{ opacity: 0.9 }}>
            Small apps, made with care
          </Typography>
        </Container>
      </Box>

      {/* Company Story */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={7}>
            <Typography variant="h3" gutterBottom>
              Our story
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}>
              Teniqua Systems started in 2025 out of a simple frustration: too many
              small apps ask for far more than they need — your account, your
              contacts, your location — to do something that should be trivial.
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}>
              So we set out to build the opposite. Our first app, CodePeek, reads QR
              codes and barcodes and tells you exactly what is inside them, with all
              the work happening on your own device. Nothing is uploaded, nothing is
              tracked. It is free, runs in any modern browser, and installs as an app
              on Android and iPhone.
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}>
              Next up is Leru, a weather app we are building for people who just want
              a clear answer about the day ahead without wading through clutter. We
              take on projects one at a time and finish them properly before moving on.
            </Typography>
          </Grid>
          <Grid item xs={12} md={5}>
            <Box
              sx={{
                backgroundColor: theme.palette.grey[100],
                p: 4,
                borderRadius: 2,
                textAlign: 'center',
              }}
            >
              <Typography variant="h3" gutterBottom color="primary.main" sx={{ fontWeight: 700 }}>
                1 live
              </Typography>
              <Typography variant="h6" gutterBottom>
                app you can use today
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                CodePeek — free, private, on-device
              </Typography>
              <Typography variant="h3" gutterBottom color="primary.main" sx={{ fontWeight: 700 }}>
                1 more
              </Typography>
              <Typography variant="h6" gutterBottom>
                on the way
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Leru, our weather app, in development
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Company Values */}
      <Box sx={{ backgroundColor: theme.palette.grey[50], py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" textAlign="center" gutterBottom>
            What we care about
          </Typography>
          <Typography variant="h6" textAlign="center" color="text.secondary" sx={{ mb: 6 }}>
            The rules we hold ourselves to on every app
          </Typography>

          <Grid container spacing={4}>
            {values.map((value, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card sx={{ height: '100%', textAlign: 'center' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ mb: 2 }}>{value.icon}</Box>
                    <Typography variant="h6" gutterBottom>
                      {value.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {value.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default About;
